import { FC, ReactNode } from 'react';
import { Button, styled } from '@mui/material';
import useInvestorChangeLog from 'hooks/data/investor/useInvestorChangeLog';
import { getFormattedAddress, dateToLabel } from 'further-ui/utils';
import { startCase } from 'lodash';
import { AccountType, Api } from 'further-types/investor';
import {
  IncomeSourceLabels,
  InvestorCustomTagCategoryLabels,
  investorTypeLabel,
} from 'further-ui/labels';
import { useLoadMoreAndHideContent } from 'hooks/ui/useLoadMoreContent';
import Table, { FooterRow } from 'components/Table';
import { signUpSourceLabel } from 'helpers/investor/getSignUpSourceLabel';

const EmptyBadge = styled('span')({
  padding: '3px 16px 2px 16px',
  borderRadius: '4px',
  border: '1px solid #c5c5c5',
  textTransform: 'uppercase',
  fontSize: '0.65rem',
  color: '#828282',
});

type Props = {
  investorId: string;
};

const getChangedByLabel = (
  changedBy: Api.InvestorChangeLogEntry['changedBy'],
  investorId: string,
) => {
  const name =
    changedBy?.firstName || changedBy?.lastName
      ? `${changedBy.firstName ?? ''} ${changedBy.lastName ?? ''}`
      : 'Unknown user';
  const isChangedBySelf =
    changedBy?.userType === 'investor' && changedBy?._id === investorId;

  if (isChangedBySelf) return 'This investor';
  if (!changedBy) return '';
  return `${name} (${startCase(changedBy?.userType)})`;
};

const fieldFormatters = new Map<string, string>([
  ['defaultAdviserId', 'Default Adviser'],
]);

const formatEntry = (
  entry: [string, any],
  multiFieldChange: boolean,
  emptyBadge: ReactNode,
) => {
  const [field, value] = entry;
  const valueFormatters = new Map<string, (item: any) => string>([
    [
      'accountType',
      (item) =>
        item === AccountType.Individual ? 'Individual' : 'Legal entity',
    ],
    [
      'customTags',
      (item) =>
        `${InvestorCustomTagCategoryLabels[item.category]}: ${item.tag}`,
    ],
    ['investorType', (item) => investorTypeLabel[item] ?? item],
    ['address', (item) => getFormattedAddress(item)],
    ['signUpSource', (item) => signUpSourceLabel[item] ?? item],
    ['incomeSources', (item) => IncomeSourceLabels[item] ?? item],
    [
      'defaultAdviserId',
      (item) =>
        item.fullName || item.email ? `${item.fullName} - ${item.email}` : item,
    ],
  ]);

  const formatStringValue = (value: unknown): string => {
    if (valueFormatters.has(field)) {
      return valueFormatters.get(field)?.(value) ?? '';
    }

    if (typeof value === 'string') {
      const dateRegex = /^\d{4}-\d{2}-\d{2}/;
      if (dateRegex.test(value)) {
        return dateToLabel(value);
      }

      return value;
    }

    return JSON.stringify(value);
  };

  const formatValue = (value: unknown): string | ReactNode => {
    if (value === null || value === undefined || value === '') {
      return emptyBadge;
    }

    if (Array.isArray(value)) {
      return (
        <>
          {value.map((item) => (
            <>
              {formatStringValue(item)}
              <br />
            </>
          ))}
        </>
      );
    }

    return formatStringValue(value);
  };

  return (
    <div>
      {multiFieldChange ? (
        <span>
          {fieldFormatters.has(field)
            ? fieldFormatters.get(field)
            : startCase(field)}
          :{' '}
        </span>
      ) : null}
      <span>{formatValue(value)}</span>
    </div>
  );
};

const InvestorChangeLog: FC<Props> = ({ investorId }) => {
  const { data, isFetching } = useInvestorChangeLog(investorId);

  const emptyBadge = <EmptyBadge>Empty</EmptyBadge>;
  const { onToggle, displayedContent } = useLoadMoreAndHideContent(data ?? []);

  const columns = [
    {
      label: 'Details changed',
      key: 'detailsChanged',
      sort: false,
      render: (elm: Api.InvestorChangeLogEntry) =>
        elm.detailsChanged.length > 1
          ? 'Various'
          : fieldFormatters.has(elm.detailsChanged[0])
          ? fieldFormatters.get(elm.detailsChanged[0])
          : startCase(elm.detailsChanged[0]),
    },
    {
      label: 'Original details',
      key: 'originalDetails',
      sort: false,
      render: (elm: Api.InvestorChangeLogEntry) =>
        Object.entries(elm.originalDetails).map((entry) =>
          formatEntry(
            entry,
            Object.keys(elm.originalDetails).length > 1,
            emptyBadge,
          ),
        ),
    },
    {
      label: 'New details',
      key: 'newDetails',
      sort: false,
      render: (elm: Api.InvestorChangeLogEntry) =>
        Object.entries(elm.newDetails).map((entry) =>
          formatEntry(
            entry,
            Object.keys(elm.newDetails).length > 1,
            emptyBadge,
          ),
        ),
    },
    {
      label: 'Date changed',
      key: 'date',
      sort: false,
      render: (elm: Api.InvestorChangeLogEntry) =>
        dateToLabel(elm.date, 'N/A', true),
    },
    {
      label: 'Changed by',
      key: 'changedBy',
      sort: false,
      render: (elm: Api.InvestorChangeLogEntry) =>
        getChangedByLabel(elm.changedBy, investorId),
    },
  ];

  return (
    <Table
      columns={columns}
      loading={isFetching}
      tablebody={displayedContent}
      variant="nohover"
      emptyMessage="This investor has no changes."
      pagination={false}
      TableFooter={
        <tfoot>
          {(data?.length ?? 0) > 10 ? (
            <FooterRow>
              <td colSpan={12} align="center">
                <Button color="primary" onClick={onToggle}>
                  {displayedContent.length > 10
                    ? 'Collapse change log table'
                    : 'Click to view all change log entries'}
                </Button>
              </td>
            </FooterRow>
          ) : null}
        </tfoot>
      }
    />
  );
};

export default InvestorChangeLog;
